import { authApi } from "@api/auth"
import { useAuth } from "@auth/AuthContext"
import { useClassrooms } from "@features/classrooms"
import { analytics } from "@lib/analytics"
import { logger } from "@lib/logger"
import { useMutation } from "@tanstack/react-query"
import { useEffect, useRef } from "react"

import type { ProfessorSettings } from "@/types"

type GateStatus = "loading" | "needs_onboarding" | "ready"

/**
 * Decides whether the active professor still needs to go through onboarding.
 *
 * Three terminal states for `settings.onboarding_completed`:
 *   - `true`  → ready (already completed)
 *   - `false` → needs_onboarding (explicit reset, e.g. via the internal-account
 *               developer "force onboarding" button — ignores classrooms count)
 *   - undefined →
 *       - zero classrooms → needs_onboarding (brand-new professor)
 *       - has classrooms  → ready, with a silent backfill PATCH to set the
 *                           flag = true (legacy migration path)
 *
 * The `false`-vs-undefined distinction is what lets the QA force-reset
 * trigger a re-run even though the professor still has classrooms.
 */
export function useOnboardingGate(): GateStatus {
  const { professor, updateProfessor } = useAuth()
  const classroomsQuery = useClassrooms()
  const backfilledRef = useRef(false)

  const flagRaw = professor?.settings?.onboarding_completed
  const explicitDone = flagRaw === true
  const explicitForce = flagRaw === false

  useEffect(() => {
    if (backfilledRef.current) return
    if (!professor) return
    if (explicitDone || explicitForce) return
    if (classroomsQuery.isLoading || classroomsQuery.isError) return
    const count = classroomsQuery.data?.length ?? 0
    if (count === 0) return
    // Legacy professor: has classrooms but missing flag. Backfill silently.
    backfilledRef.current = true
    const nextSettings: ProfessorSettings = {
      ...(professor.settings ?? {}),
      onboarding_completed: true,
      onboarding_completed_at: new Date().toISOString(),
    }
    authApi
      .updateMe({ settings: nextSettings })
      .then(() => {
        updateProfessor({ settings: nextSettings })
        logger.info("onboarding.backfill_succeeded")
      })
      .catch((error: unknown) => {
        logger.warning("onboarding.backfill_failed", { error: String(error) })
      })
  }, [
    professor,
    explicitDone,
    explicitForce,
    classroomsQuery.isLoading,
    classroomsQuery.isError,
    classroomsQuery.data,
    updateProfessor,
  ])

  if (!professor) return "loading"
  if (explicitDone) return "ready"
  if (explicitForce) return "needs_onboarding"
  if (classroomsQuery.isLoading) return "loading"
  // On error fetching classrooms, fail OPEN — let the professor through to
  // the app. The error will surface on the destination page; we don't want
  // to trap a returning user behind a transient network blip.
  if (classroomsQuery.isError) return "ready"
  const count = classroomsQuery.data?.length ?? 0
  if (count === 0) return "needs_onboarding"
  return "ready"
}

interface CompleteOnboardingArgs {
  classroomId: string
  studentsAdded: number
}

/**
 * QA-only: reset the onboarding flag to `false` so the wizard fires again on
 * the next page load. Available to internal accounts (`is_internal_account`
 * off /api/auth/me, PRD-22 §FR-18) via the developer card on the Settings
 * page. Not exposed to real professors. The gate distinguishes `false` from
 * `undefined` so the reset triggers the wizard even when the professor
 * already has classrooms.
 */
export function useResetOnboarding() {
  const { professor, updateProfessor } = useAuth()
  return useMutation({
    mutationFn: async () => {
      if (!professor) throw new Error("no_professor")
      const nextSettings: ProfessorSettings = {
        ...(professor.settings ?? {}),
        onboarding_completed: false,
      }
      // Drop the timestamp so a fresh completion writes a new one.
      delete nextSettings.onboarding_completed_at
      const updated = await authApi.updateMe({ settings: nextSettings })
      updateProfessor(updated)
      // No email in the payload — logging-discipline.md §C-11. The actor is
      // already in the audit row the PATCH wrote server-side.
      logger.info("onboarding.force_reset", { professorId: professor.id })
      return updated
    },
  })
}

/**
 * Idempotent completion mutation. Writes the onboarding flag through
 * PATCH /api/auth/me, merging existing settings to preserve theme /
 * language / notification keys (PRD-03 FR-23).
 */
export function useCompleteOnboarding() {
  const { professor, updateProfessor } = useAuth()
  return useMutation({
    mutationFn: async (args: CompleteOnboardingArgs) => {
      if (!professor) throw new Error("no_professor")
      const nextSettings: ProfessorSettings = {
        ...(professor.settings ?? {}),
        onboarding_completed: true,
        onboarding_completed_at: new Date().toISOString(),
      }
      const updated = await authApi.updateMe({ settings: nextSettings })
      updateProfessor(updated)
      logger.info("onboarding.completed", {
        classroomId: args.classroomId,
        studentsAdded: args.studentsAdded,
      })
      return updated
    },
    onSuccess: (_updated, args) => {
      analytics.track("onboarding_completed", {
        students_added: args.studentsAdded,
      })
    },
  })
}
